import React, { useCallback, useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import StopCircle from '@mui/icons-material/StopCircle';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import Tooltip from '@mui/material/Tooltip';
import SettingsOverscanIcon from '@mui/icons-material/SettingsOverscan';
import { Stack } from '@mui/material';
import LastPrompts from './LastPrompts';
import StreamStatistics from './StreamStatistics';
import { getConnectionStateColor } from '@lib/models/enums';
import { useAiChatServices } from '../AiChatContext';
import { CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { ClientMessage } from '@lib/models/ClientMessage';
// Inline-edit dialog was dropped in the v0.1 carve; stub keeps callers compiling.
const PromptEditorDialog: React.FC<any> = () => null;
import { useAiChatSessionStore } from '../AiChatContext';
import { ConnectionState } from '@lib/models/connectionState';
import { PauseCircle, PlayCircle } from '@mui/icons-material';
import { AgentGatewayWSAsync } from '@lib/services/servers/AgentGatewayWSAsync';
import { TaskList } from './TaskList';
import ModelSelector from './ModelSelector';
// ChatSettings popover was dropped in the v0.1 carve; stub keeps callers compiling.
const ChatSettings: React.FC<any> = () => null;
import { useContainerRectangle } from '@lib/components';

interface PromptProps {
  forceMessage?: string | null;
  prompt?: string;
  isTurnOpen?: boolean;
  onHeightChange?: (newHeight: number) => void;
}

const Prompt: React.FC<PromptProps> = ({ forceMessage, onHeightChange }) => {
  const [isLandscapeInSmallScreen, setIsLandscapeInSmallScreen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLessThenMedium = useMediaQuery("(max-width: 900px)");

  const [isSending, setIsSending] = useState<boolean | null>(false);
  const [isSuspending, setIsSuspending] = useState<boolean | null>(false);
  const [isSuspended, setIsSuspended] = useState<boolean>(false);
  const [forceTooltip, setForceTooltip] = useState<boolean>(false);
  const [forceExpanded, setForceExpanded] = useState<number>(0);

  const promptContainer = useRef<HTMLDivElement>(null);
  const [containerWidth, containerHeight] = useContainerRectangle(promptContainer, new Date().getTime().toString());

  useEffect(() => {
    if (onHeightChange)
      onHeightChange(containerHeight);
  }, [containerHeight])


  const [messageNumber, setMessageNumber] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { agentGatewayService, showPromptHistory, showStatistics, showModelSelector, showSessionInfo, showTaskList } = useAiChatServices();

  const [lastTimeout, setLastTimeout] = useState<NodeJS.Timeout | null>(null);

  const { notificationPort } = useAiChatServices();
  const pushNotification = notificationPort.pushNotification.bind(notificationPort);

  const isTurnOpen = useAiChatSessionStore((state) => state.turnOpen);
  const connectionStatus = useAiChatSessionStore((state) => state.connectionStatus);
  const currentSession = useAiChatSessionStore((state) => state.session);

  const lastToolEvent = useAiChatSessionStore((state) => state.lastToolEvent);

  let hasTodoList = false;
  if (lastToolEvent?.eventName === 'todolistUpdated' && lastToolEvent?.eventData !== "") {
    hasTodoList = true;
  }

  // hasTodoList  =true;

  // Prompt editor dialog state
  const [isPromptEditorOpen, setIsPromptEditorOpen] = useState(false);

  useEffect(() => {
    if (isMobile && window) {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      setIsLandscapeInSmallScreen(windowWidth > windowHeight && windowHeight < 400);
    }
  }, [isMobile]);

  // Auto-focus input field when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);//test

  useEffect(() => {
    if (!isTurnOpen) {
      setIsSuspended(false);
      setIsSuspending(false);
    }
  }, [currentSession?.id])

  const awaitingState = isTurnOpen || isSending;

  const handleSend = (messageOverride?: string, stopOverride: boolean = false) => {
    if (!agentGatewayService) {
      pushNotification({
        message: "Agent gateway is not available",
        type: 'error'
      })
      return;
    }

    if (awaitingState && !stopOverride) {
      setForceTooltip(true);
      setTimeout(() => {
        setForceTooltip(false);
      }, 4000);
      return;
    }

    let message = messageOverride ?? inputRef.current?.value ?? '';
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    if (isSuspended) {
      setIsSuspended(false);
      if (message.trim() == "")
        message = "Continue, por favor";
    }

    setMessageNumber(messageNumber + 1);

    setIsSending(() => true);
    // let totalTimer = InteractionPartUtils.createWaiting(true, true, "Overall", new Date());

    // agentGatewayService.sendStatusMessage(message, [totalTimer], messageNumber.toString() + "1");


    if (!currentSession) {
      pushNotification({
        message: "There is no session to send a message",
        type: 'error'
      })
    }

    agentGatewayService.sendMessage(new ClientMessage(message, "text/plain"))
      .catch((error) => {
        console.error("Prompt send", error)
      })
      .finally(() => {
        setIsSending(() => false);
        setForceTooltip(false);
        setForceExpanded((p) => p + 1);
      })

  };

  //handle force message
  useEffect(() => {
    if (forceMessage) {
      handleSend(forceMessage);
    }
  }, [forceMessage]);

  const handlePromptSelected = useCallback((promptText: string, promptJson?: string) => {
    if (inputRef.current) {
      inputRef.current.value = promptText;
    }
    setForceExpanded((p) => p + 1);
  }, []);

  // Prompt editor dialog handlers
  const handleOpenEditorOrPauseOrSuspend = useCallback(() => {
    if (awaitingState) {
      if (!agentGatewayService)
        return;

      if (agentGatewayService instanceof AgentGatewayWSAsync) {

        setIsSuspending(true);
        agentGatewayService.suspend()
          .then(() => {
            setIsSuspended(true);
            setIsSending(false);
          })
          .catch((error: any) => {
            pushNotification({
              message: "Error suspending",
              type: 'error',
              details: error.toString()
            })
            console.error("Error suspending", error.toString());
          })
          .finally(() => {
            setIsSuspending(false);
          })
      }
    }
    else {
      if (isSuspended) {
        if (agentGatewayService instanceof AgentGatewayWSAsync) {
          setIsSuspended(false);

          agentGatewayService.cancelSuspend()
        }
      }
      else {
        setIsPromptEditorOpen(true);
      }
    }
  }, [isTurnOpen, isSuspended, awaitingState]);

  const handleClosePromptEditor = useCallback(() => {
    setIsPromptEditorOpen(false);
  }, []);

  const handlePromptApply = useCallback((promptText: string) => {
    if (inputRef.current) {
      inputRef.current.value = promptText;
    }
  }, []);

  const handlePromptApplyAndSend = useCallback(async (promptText: string) => {
    if (inputRef.current) {
      inputRef.current.value = promptText;
    }
    handleSend(promptText);
  }, [handleSend]);

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };


  const handleBlur = useCallback(() => {
    // Use a timeout to allow clicking on prompt history items
    setLastTimeout(setTimeout(() => {
      setForceExpanded((p) => p + 1);
    }, 150));
  }, [lastTimeout]);

  const cancelTimeout = useCallback(() => {
    if (lastTimeout) {
      clearTimeout(lastTimeout);
      setLastTimeout(null);
    }
  }, [lastTimeout]);

  const getConnectionIcon = () => {
    if (awaitingState) {
      return <CircularProgress size={18} sx={{ color: 'rgba(255, 255, 255, 1)' }} />;
    }
    if (isSuspended) {
      return <PlayCircle fontSize="medium" />;
    }
    return <SendIcon fontSize="small" />;
  };

  const getPlaceholder = () => {
    return "";
  };

  const getTooltipText = () => {
    if (isTurnOpen) {
      return "Aguarde a resposta do agente...";
    }
    return "Click to send or hit ENTER.";
  };


  if (!agentGatewayService || isLandscapeInSmallScreen) {
    return null;
  }


  function getCurrentState(): string {
    return isSending ? "Sending message" :
      isTurnOpen ? "Suspend execution" : "Open prompt editor"
  }

  const isConnecting = connectionStatus == ConnectionState.Connecting

  const baseStyle = {
    overflow: 'auto',
    borderTop: `1px solid rgba(50, 50, 50)`,
    bgcolor: 'rgba(30, 30, 30)',
    backdropFilter: "blur(2px)",
    zIndex: 200,
    mt: 1,
    paddingTop: 0.5,
    paddingBottom: 0.5,
    paddingLeft: 1,
    paddingRight: 1,

    position: 'relative',
    width: 'calc(100% + 20px)',
    left: '-16px'
  };

  let resolvedTyle = baseStyle;

  return (
    <Box ref={promptContainer} sx={{ mt: 1.5, alignItems: 'center', width: '100%', position: "relative" }}>
      <Box sx={{
        borderRadius: '5px',
        padding: '1px',
        display: 'flex',
        alignItems: 'flex-start',
        border: `1px solid ${getConnectionStateColor(connectionStatus, theme)}`,
        // borderRadius: 2,
        zIndex: "100",
        position: 'relative',
        bgcolor: 'background.paper',
        opacity: 1,
        transition: 'all 0.1s ease',

        // Animated border effect only when connecting
        ...(isConnecting && {
          animation: 'rotate 4s linear infinite',
          background: `conic-gradient(from var(--angle),
          ${theme.palette.info.dark},
          ${theme.palette.info.dark},
          ${theme.palette.info.dark},
          ${theme.palette.info.dark},
          #fff 25%,
          ${theme.palette.info.dark},
          ${theme.palette.info.dark},
          ${theme.palette.info.dark},
          ${theme.palette.info.dark}
           )
          `,
        }),
      }}>
        {/* Action Buttons Stack */}
        <Stack
          direction="column"
          sx={{
            alignSelf: 'flex-end',
            bgcolor: 'background.paper',
          }}
        >
          {showModelSelector !== false && <ModelSelector />}
          {showSessionInfo !== false && <ChatSettings />}
        </Stack>

        <TextField
          disabled={connectionStatus === ConnectionState.Idle}
          inputRef={inputRef}
          multiline
          minRows={3}
          maxRows={8}
          variant="standard"
          defaultValue={getPlaceholder()}
          size="medium"
          sx={{
            pt: 0.2,
            textWrap: 'wrap',
            backgroundColor: 'background.paper',
            flex: 1,
            fontFamily: theme.typography.fontFamily,
            '& .MuiInputBase-input': {
              fontSize: isLessThenMedium ?
                isMobile ? '1.2rem' : '1rem' : '0.8rem',
              lineHeight: isLessThenMedium ?
                isMobile ? 1.2 : 1.2
                : 1.5,
              fontFamily: theme.typography.fontFamily,
              paddingTop: '4px',
              // Additional mobile-specific styles
              ...(isMobile && {
                fontSize: isLessThenMedium ?
                  isMobile ? '1.2rem' : '0.8rem' : '0.8rem',
                WebkitTextSizeAdjust: '100%',
                MozTextSizeAdjust: '100%',
                msTextSizeAdjust: '100%'
              })
            },
            '& .MuiInputBase-root.Mui-disabled': {
              '& .MuiInputBase-input': {
                color: `${theme.palette.text.disabled} !important`,
                WebkitTextFillColor: `${theme.palette.text.disabled} !important`
              },
              '& .MuiInputBase-input::placeholder': {
                color: `${theme.palette.text.disabled} !important`,
                WebkitTextFillColor: `${theme.palette.text.disabled} !important`,
                opacity: 1
              }
            }
          }}
          slotProps={{
            input: {
              disableUnderline: true,
              style: {
                fontFamily: theme.typography.fontFamily,
                fontSize: isLessThenMedium ?
                  isMobile ? '1.2rem' : '1rem' : '0.8rem',
                lineHeight: isLessThenMedium ?
                  isMobile ? 1 : 1.2
                  : 1.5,
                paddingTop: '4px',
                // Additional mobile-specific styles
                ...(isMobile && {
                  fontSize: isLessThenMedium ?
                    isMobile ? '1.2rem' : '1rem' : '0.8rem',
                  WebkitTextSizeAdjust: '100%',
                  MozTextSizeAdjust: '100%',
                  msTextSizeAdjust: '100%'
                })
              }
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />

        <Stack
          direction="column"
          sx={{
            p: 1.2,
            pt: 0,
            bgcolor: 'background.paper',
            alignSelf: 'flex-end',
          }}
        >
          <Tooltip title={getTooltipText()} placement="top" open={forceTooltip}>
            <IconButton
              color="primary"
              size="small"
              sx={{
                borderRadius: 1,
                mx: 0.5,
                pt: 1.3,
                fontSize: '1rem',
                alignContent: 'top',
                color: 'primary.main'
              }}
              onClick={async () => await handleSend()}>{
              }
              {getConnectionIcon()}
            </IconButton>
          </Tooltip>
          <Tooltip title={getCurrentState()} placement="top">
            <IconButton
              size="small"
              onClick={handleOpenEditorOrPauseOrSuspend}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
                p: 0.5,
                marginTop: '0px !important'
              }}
            >
              {(awaitingState) && (
                <Box sx={{ position: 'relative', p: 0, mt: 0 }}>
                  <PauseCircle sx={{ p: 0, mt: 0 }} />
                  {isSuspending && (
                    <CircularProgress
                      size="1.5rem"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                      }}
                    />
                  )}
                </Box>
              )}
              {!awaitingState && isSuspended && (
                <StopCircle sx={{ p: 0, mt: 0 }} />
              )}
              {!awaitingState && !isSuspended && <SettingsOverscanIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>


      {showPromptHistory && !(hasTodoList && showTaskList) && (
        <LastPrompts
          cancelTimeout={cancelTimeout}
          onPromptSelected={handlePromptSelected}
          forceExpanded={forceExpanded}
        >
          {showStatistics && <StreamStatistics />}
        </LastPrompts>

      )}
      {(showTaskList !== false && isTurnOpen && hasTodoList) &&
        (<Box sx={isTurnOpen ? resolvedTyle : { display: '' }}>
          <TaskList />
        </Box>)
      }

      {/* Prompt Editor Dialog */}
      <PromptEditorDialog
        open={isPromptEditorOpen}
        onClose={handleClosePromptEditor}
        initialPrompt={inputRef.current?.value || ''}
        onApply={handlePromptApply}
        onApplyAndSend={handlePromptApplyAndSend}
      />

    </Box >

  );
};

export default Prompt; 